import React, { useState, useEffect, useMemo } from "react";
import { OwnerAndEmployeeCensusType, SingleCensusType } from "types";
import FooterButton from "uicomponents/FooterButton";
import {
  trackPageViewUserStep,
  trackBrokenKafkaPrefills,
} from "utils/analytics/trackEvent";
import styles from "./OwnerAndEmployeeCensus.module.css";
import {
  BACK_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
  LOADING_BUTTON_TEXT,
} from "pages/WorkersCompApplcation/utils/contants";
import CensusDrawer from "uicomponents/CensusDrawerWC";
import { Modal } from "@justworkshr/milo-components";
import Prefilled from "uicomponents/CensusTable/Prefilled";
import CensusNotice from "uicomponents/CensusNotice";
import { validateMember } from "./utils/validateMember";
import { hasQueryParam } from "utils/helper";
interface IOwnerEmployeeCensusProps {
  setNextStep: () => void;
  setPreviousStep: () => void;
  data: OwnerAndEmployeeCensusType;
  refetchData: () => void;
}

interface IDrawerState {
  status: string;
  initialData?: SingleCensusType;
}

const OwnerEmployeeCensus = ({
  ...props
}: IOwnerEmployeeCensusProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(3, "owners");
    trackPageViewUserStep(4, "employee");
  }, []);

  const { setNextStep, setPreviousStep, data } = props;

  const unsubmittedStatuses = ["IN_PROGRESS", "NOT_STARTED", undefined];
  const wcAppUnsubmitted = unsubmittedStatuses.includes(
    data?.workersCompApplication?.[0]?.status || undefined
  );
  const hiAppUnsubmitted = unsubmittedStatuses.includes(
    data?.healthInsuranceApplication?.[0]?.status || undefined
  );
  const isCoreApp = hasQueryParam("usecoreapp");
  const showCensusNotice = hiAppUnsubmitted && wcAppUnsubmitted && isCoreApp;

  const [hasError, setHasError] = useState(false);
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);
  const [drawerState, setDrawerState] = useState<IDrawerState>({
    status: "closed",
    initialData: undefined,
  });

  useEffect(() => {
    data?.employeeCensuses?.forEach((emp) => {
      if (emp?.email === "-") {
        trackBrokenKafkaPrefills("Prospect blocked by kafka prefill error");
      }
    });
  }, [data?.employeeCensuses]);

  const areAllMembersValid = useMemo(() => {
    if (!data?.employeeCensuses) {
      return true;
    }
    return data.employeeCensuses.every(validateMember);
  }, [data?.employeeCensuses]);

  useEffect(() => {
    if (areAllMembersValid) {
      setHasError(false);
    }
  }, [areAllMembersValid]);

  const handleBack = (): void => {
    setPreviousStep();
  };

  const handleNext = () => {
    if (!areAllMembersValid) {
      setHasError(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setHasError(false);
    setIsLoadingSteps(true);
    setNextStep();
  };

  const openDrawer = (initialData?: SingleCensusType) => () => {
    setDrawerState({
      status: "animating",
      initialData,
    });
    setTimeout(() => {
      setDrawerState({
        initialData,
        status: "open",
      });
    }, 10);
  };

  const closeDrawer = (refetch?: boolean) => {
    setHasError(false);
    setDrawerState({
      ...drawerState,
      status: "animating",
    });
    setTimeout(() => {
      setDrawerState({
        status: "closed",
      });
    }, 500);
    if (refetch) props.refetchData();
  };

  const currentInitialData = drawerState.initialData
    ? data?.employeeCensuses?.find(
        (employee) => employee === drawerState.initialData
      )
    : undefined;

  return (
    <>
      <div className={styles.title}>Update your team info</div>
      <div className={styles.description}>
        <p>
          We’ve prefilled the info you’ve already given us but need a little
          more.
        </p>
      </div>

      <Prefilled
        censuses={
          data?.employeeCensuses?.map((emp) => ({
            complete: validateMember(emp),
            censusData: emp,
          })) || []
        }
        onUpdateClick={(employeeData) =>
          openDrawer(employeeData as SingleCensusType)()
        }
        hasError={hasError}
      />

      <Modal
        isOpen={drawerState.status !== "closed"}
        onClose={() => closeDrawer()}
      >
        {drawerState.status !== "closed" && (
          <CensusDrawer
            isOpen={drawerState.status === "open"}
            initialData={currentInitialData}
            closeDrawer={closeDrawer}
            officeLocations={data?.officeLocations}
          />
        )}
      </Modal>

      {showCensusNotice && <CensusNotice className={styles.notice} />}

      <FooterButton
        nextButtonText={isLoadingSteps ? LOADING_BUTTON_TEXT : NEXT_BUTTON_TEXT}
        backButtonText={BACK_BUTTON_TEXT}
        buttonType="button"
        handleBackClick={handleBack}
        handleNextClick={handleNext}
        disabled={isLoadingSteps}
      />
    </>
  );
};

export default OwnerEmployeeCensus;
